import { createContext, useState, ReactNode } from 'react'

import { Token, AuthContextType } from '../types/context.types'
import { logInfo, logToken } from '../sso/utils/sso-logger'
import { clearSuiteAccessTokenCache } from '../sso/services/suite-access-token.service'
import { clearPayAccessTokenCache } from '../sso/services/pay-auth-bridge.service'

export const AuthContext = createContext<Partial<AuthContextType>>({})

const Provider = ({ children }: { children: ReactNode }) => {
  const [isAuth, setIsAuth] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const token = window.sessionStorage.getItem('token') || window.localStorage.getItem('token') || ''
      logInfo('AuthContext — initial isAuth from storage', { hasToken: Boolean(token) })
      logToken('AuthContext — initial token', token)
      return token
    }
    return ''
  })
  const [isSync, setIsSync] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const storedValue = window.sessionStorage.getItem('isSync')
      return storedValue ? storedValue === 'true' : false
    }
    return false
  })

  const value: AuthContextType = {
    isAuth,
    isSync,
    updateSyncStatus: (syncStatus: boolean) => {
      logInfo('AuthContext — updateSyncStatus', { syncStatus })
      if (typeof window !== 'undefined') {
        setIsSync(syncStatus)
        window.sessionStorage.setItem('isSync', String(syncStatus))
        return true
      }

      return false
    },
    activateAuth: (systemUserLogged: Token) => {
      logInfo('AuthContext — activateAuth')
      logToken('AuthContext — storing Pay refresh token', systemUserLogged.token)
      if (typeof window !== 'undefined') {
        setIsAuth(systemUserLogged.token)
        window.sessionStorage.setItem('token', JSON.stringify(systemUserLogged.token))
        return true
      }

      return false
    },
    removeAuth: () => {
      logInfo('AuthContext — removeAuth (clearing Pay session storage)')
      clearSuiteAccessTokenCache()
      clearPayAccessTokenCache()
      setIsAuth('')
      window.localStorage.removeItem('token')
      window.sessionStorage.removeItem('token')
      window.sessionStorage.removeItem('isSync')
      window.sessionStorage.removeItem('__ldbs')
      window.sessionStorage.removeItem('__ldbssc')
      window.sessionStorage.removeItem('BBVABalance')
      window.sessionStorage.removeItem('userSession')
      window.localStorage.removeItem('userSession')
      window.sessionStorage.removeItem('cobreBalance')
      window.sessionStorage.removeItem('USDDollarRate')
      window.sessionStorage.removeItem('irisBalance')
      window.sessionStorage.removeItem('cobreBalanceV3')
      window.sessionStorage.removeItem('refreshDatabase')
      window.sessionStorage.removeItem('AVVillasBalance')
      window.sessionStorage.removeItem('pocketsFirstLoaded')
      window.sessionStorage.removeItem('banksBalancesDataLoaded')
      window.sessionStorage.removeItem('suiteDashboardReady')
      logInfo('AuthContext — removeAuth done')
    },
    clearLegacyPayToken: () => {
      logInfo('AuthContext — clearLegacyPayToken (suite SSO session; identity owns auth)')
      clearSuiteAccessTokenCache()
      clearPayAccessTokenCache()
      setIsAuth('')
      window.localStorage.removeItem('token')
      window.sessionStorage.removeItem('token')
    }
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const AuthContextProvider = {
  Provider,
  Consumer: AuthContext.Consumer
}

export default AuthContextProvider
